import React, { useId, useLayoutEffect, useRef, useState } from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import { Box, ButtonBase, Stack, Tab, Tabs, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { SectionHeading, SurfacePlate } from '~/pages/LeLens/surface';

import type { LensCreateResumableSession } from '~/hooks/apis/reporting/useGetLensCreateSessions';

import type { LensPlanMode } from '~/types/leLens';

import { formatRelativeTime } from './SessionHistoryPanel';
import { LENS_SHAPES } from './lensShapes';
import { SERIF_ACCENT_FONT } from './uiTokens';

export type LensCreateEmptyStateProps = {
  /** Sends a revealed example as the same create turn as typing it and pressing Enter. */
  onPickBrief?: (brief: string, mode: LensPlanMode) => void;
  /** Up to three unfinished creates, newest first: "Pick up where you left off" only renders when
   *  this is non-empty. A `built` one renders with an "Open Lens" action instead of a resume. */
  resumableSessions?: ReadonlyArray<LensCreateResumableSession>;
  /** Loads that session into the sheet, the same way clicking it in the Threads rail would. */
  onResumeSession?: (sessionId: string) => void;
  /** A `built` resumable row's own action: opens the finished Lens instead of the create sheet. */
  onOpenLens?: (lensId: string) => void;
  /** LeDataChat's own composer (F2 owns it), rendered as the hero directly under the heading and
   *  above the examples, matching the canvas. Optional so this component still renders sensibly on
   *  its own (e.g. in a snapshot/story) with nothing passed. */
  composerSlot?: React.ReactNode;
};

const COMPACT_RESUMABLE_SESSIONS = 3;

function resumeStatusLine(status: LensCreateResumableSession['status']): string {
  switch (status) {
    case 'needs_answer':
      return 'Needs your answer';
    case 'built':
      return 'Built';
    default:
      return 'In progress';
  }
}

export default function LensCreateEmptyState({
  composerSlot,
  onOpenLens,
  onPickBrief,
  onResumeSession,
  resumableSessions = [],
}: LensCreateEmptyStateProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const now = Date.now();
  const panelId = useId();
  const [startPanel, setStartPanel] = useState<'recent' | 'examples'>('recent');
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelHeight, setPanelHeight] = useState<number>();

  const visibleResumable = resumableSessions.slice(0, COMPACT_RESUMABLE_SESSIONS);
  const visibleStartPanel = visibleResumable.length > 0 ? startPanel : 'examples';

  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel || typeof ResizeObserver === 'undefined') {
      return;
    }
    const measure = () => setPanelHeight(panel.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(panel);
    return () => observer.disconnect();
  }, [visibleStartPanel]);

  return (
    <Box
      sx={{
        alignItems: 'stretch',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        maxWidth: 960,
        mx: 'auto',
        py: 0,
        textAlign: 'left',
        width: '100%',
      }}
    >
      <Box
        sx={{
          alignItems: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          pb: 2,
          pt: { md: 1, xs: 0 },
          width: '100%',
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontFamily: SERIF_ACCENT_FONT,
            color: tokens.accent,
            fontSize: { sm: 34, xs: 28 },
            fontWeight: 400,
            letterSpacing: '-.01em',
            lineHeight: 1.1,
          }}
        >
          What do you want to track or explain?
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: 14, lineHeight: 1.5, mt: 1, maxWidth: 660 }}>
          Turn a business question into a reusable Lens. Describe what you need, or start with an example below.
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gap: { md: 3, xs: 2 },
            gridTemplateColumns: { md: 'minmax(0, 1fr) 220px', xs: 'minmax(0, 1fr)' },
            mt: 3,
          }}
        >
          {composerSlot ? (
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: tokens.accent, fontSize: 13, fontWeight: 650, mb: 1 }}>
                Describe your Lens
              </Typography>
              {composerSlot}
              <Typography color="text.secondary" sx={{ fontSize: 12, lineHeight: 1.45, mt: 1 }}>
                You can keep refining your Lens in the editor.
              </Typography>
            </Box>
          ) : null}
          <Box
            component="aside"
            aria-label="Writing a Lens brief"
            sx={{
              borderLeft: { md: `1px solid ${tokens.grid}`, xs: 0 },
              borderTop: { md: 0, xs: `1px solid ${tokens.grid}` },
              pl: { md: 2.5, xs: 0 },
              pt: { md: 0, xs: 1.5 },
            }}
          >
            <Typography sx={{ color: tokens.accent, fontSize: 13, fontWeight: 650, mb: 1 }}>
              Shape your Lens
            </Typography>
            <Stack spacing={1.25}>
              {[
                ['What to track', 'Bookings, TTV or margin'],
                ['How to break it down', 'By product, market or channel'],
                ['Time frame & comparison', 'This month, compared with last month'],
              ].map(([label, detail]) => (
                <Box key={label}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.4 }}>{label}</Typography>
                  <Typography color="text.secondary" sx={{ fontSize: 12, lineHeight: 1.4, mt: 0.25 }}>
                    {detail}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>

        <Box sx={{ mt: 3, width: '100%' }}>
          <SectionHeading eyebrow="Continue or start" />
          <SurfacePlate sx={{ mt: 1.25, overflow: 'hidden', p: 0 }}>
            <Tabs
              aria-label="Choose a starting point"
              onChange={(_event, panel: 'recent' | 'examples') => setStartPanel(panel)}
              value={visibleStartPanel}
              variant="fullWidth"
              sx={{
                borderBottom: `1px solid ${alpha(tokens.accent, 0.1)}`,
                minHeight: 40,
                '& .MuiTabs-indicator': { backgroundColor: tokens.accent },
              }}
            >
              {(['recent', 'examples'] as const).map((panel) => {
                const disabled = panel === 'recent' && visibleResumable.length === 0;
                const selected = visibleStartPanel === panel;
                return (
                  <Tab
                    aria-controls={`${panelId}-${panel}-panel`}
                    disabled={disabled}
                    id={`${panelId}-${panel}-tab`}
                    key={panel}
                    label={panel === 'recent' ? 'Recent' : 'Example questions'}
                    value={panel}
                    sx={{
                      backgroundColor: selected ? alpha(tokens.accent, 0.08) : 'transparent',
                      color: selected ? tokens.accent : 'text.secondary',
                      fontSize: 12.5,
                      fontWeight: selected ? 700 : 500,
                      minHeight: 40,
                      textTransform: 'none',
                      '&.Mui-selected': { color: tokens.accent },
                      '&:hover': { backgroundColor: tokens.plateHover },
                      '&:focus-visible': { outline: `2px solid ${tokens.accent}`, outlineOffset: -2 },
                    }}
                  />
                );
              })}
            </Tabs>

            {/* Follow the active panel's natural height without a sudden sheet resize. The
             *  message pane remains the only scroll owner when the sheet reaches its bound. */}
            <Box
              sx={{
                height: panelHeight ?? 'auto',
                overflow: 'hidden',
                transition: 'height 180ms ease',
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              }}
            >
              <Box ref={panelRef}>
                {visibleStartPanel === 'recent' ? (
                  <Box
                    aria-labelledby={`${panelId}-recent-tab`}
                    data-testid="recent-lens-panel"
                    id={`${panelId}-recent-panel`}
                    role="tabpanel"
                  >
                    {visibleResumable.map((session) => {
                      const built = session.status === 'built';
                      return (
                        <ButtonBase
                          key={session.sessionId}
                          onClick={() => {
                            if (built) {
                              if (session.lensId) {
                                onOpenLens?.(session.lensId);
                              }
                              return;
                            }
                            onResumeSession?.(session.sessionId);
                          }}
                          sx={{
                            borderTop: `1px solid ${alpha(tokens.accent, 0.1)}`,
                            display: 'grid',
                            columnGap: 1.25,
                            gridTemplateColumns: { sm: 'minmax(0, 1fr) auto auto', xs: 'minmax(0, 1fr) auto' },
                            minHeight: 56,
                            px: 1.5,
                            py: 1,
                            rowGap: 0.75,
                            textAlign: 'left',
                            width: '100%',
                            '&:first-of-type': { borderTop: 0 },
                            '&:hover': { backgroundColor: tokens.plateHover },
                            '&:focus-visible': { outline: `2px solid ${tokens.accent}`, outlineOffset: -2 },
                          }}
                        >
                          <Typography
                            noWrap
                            sx={{
                              fontSize: 13.5,
                              fontWeight: 500,
                              gridColumn: { sm: 'auto', xs: '1 / -1' },
                              minWidth: 0,
                            }}
                          >
                            {session.title ?? session.brief}
                          </Typography>
                          <Typography
                            component="span"
                            sx={{
                              justifySelf: 'start',
                              backgroundColor: built ? alpha(tokens.accent, 0.08) : 'transparent',
                              border: built ? 0 : `1px solid ${tokens.grid}`,
                              borderRadius: '999px',
                              color: built ? tokens.accent : 'text.secondary',
                              fontSize: 11.5,
                              px: 1.1,
                              py: 0.3,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {resumeStatusLine(session.status)}
                            {built && session.lensId ? ' · Open Lens' : ''}
                          </Typography>
                          <Typography color="text.secondary" noWrap sx={{ fontSize: 11.5, textAlign: 'right' }}>
                            {formatRelativeTime(session.updatedAt, now)}
                          </Typography>
                        </ButtonBase>
                      );
                    })}
                  </Box>
                ) : (
                  <Stack
                    alignItems="stretch"
                    aria-labelledby={`${panelId}-examples-tab`}
                    direction={{ md: 'row', xs: 'column' }}
                    id={`${panelId}-examples-panel`}
                    role="tabpanel"
                  >
                    {LENS_SHAPES.map((shape, shapeIndex) => (
                      <Box
                        key={shape.mode}
                        sx={{
                          borderLeft: { md: shapeIndex === 0 ? 0 : `1px solid ${alpha(tokens.accent, 0.1)}`, xs: 0 },
                          borderTop: { md: 0, xs: shapeIndex === 0 ? 0 : `1px solid ${alpha(tokens.accent, 0.1)}` },
                          flex: 1,
                          minWidth: 0,
                          p: 1.5,
                          textAlign: 'left',
                        }}
                      >
                        <Typography component="h3" sx={{ color: tokens.accent, fontSize: 13, fontWeight: 700 }}>
                          {shape.label}
                        </Typography>
                        <Typography color="text.secondary" sx={{ fontSize: 12, lineHeight: 1.4, mb: 1.25, mt: 0.4 }}>
                          {shape.description}
                        </Typography>
                        {shape.examples.map((example, exampleIndex) => (
                          <ButtonBase
                            key={example}
                            onClick={() => onPickBrief?.(example, shape.mode)}
                            sx={{
                              border: `1px solid ${alpha(tokens.accent, 0.16)}`,
                              borderRadius: 0.75,
                              display: 'flex',
                              fontSize: 12.5,
                              gap: 0.75,
                              justifyContent: 'space-between',
                              lineHeight: 1.3,
                              minHeight: 48,
                              mt: exampleIndex === 0 ? 0 : 0.75,
                              px: 1,
                              py: 0.75,
                              textAlign: 'left',
                              width: '100%',
                              '&:hover': {
                                backgroundColor: tokens.plateHover,
                                borderColor: alpha(tokens.accent, 0.36),
                              },
                              '&:focus-visible': { outline: `2px solid ${tokens.accent}`, outlineOffset: -2 },
                            }}
                          >
                            <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                              {example}
                            </Box>
                            <Box aria-hidden component="span" sx={{ color: tokens.accent, flexShrink: 0 }}>
                              →
                            </Box>
                          </ButtonBase>
                        ))}
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            </Box>
          </SurfacePlate>
        </Box>
      </Box>
    </Box>
  );
}
